import { inject } from '@angular/core';
import { ResolveFn } from '@angular/router';
import { forkJoin, map, Observable, throwError } from 'rxjs';
import { CollaborateurService } from '@shared/services/cabinet/collaborateur.service';
import { DossierService } from '@shared/services/cabinet/dossier.service';
import { DossierContext } from '@shared/models/dossier-extended-dto.model';
import { ClientDossiersService } from './client-dossiers.service';
import { saisieTravailAutorisee } from './saisie-travail';

/**
 * Résolution du dossier courant (route `dossier/:id`).
 * - Client : ne charge QUE le dossier demandé (au lieu de tous, cf. ClientDossiersService).
 * - Collaborateur : dossier, travail en cours et situations (API Cabinet, contrôleur Dossier), comme l'ancien
 *   dossierResolver.
 */
export const chargementDossierResolver: ResolveFn<DossierContext> = (route) => {
  const dossierService = inject(DossierService);
  const id = +route.paramMap.get('id')!;
  if (!inject(CollaborateurService).isClient) {
    return resoudreDossierCollab(id);
  }
  const clientDossiers = inject(ClientDossiersService);
  const cabinetId = dossierService.cabinetId;
  // Même règle que l'ancien resolver (voir ClientDossiersService.accesPour).
  const access = clientDossiers.accesPour(id, cabinetId);

  if (!access) {
    return throwError(() => new Error(`Dossier ${id}, cabinet: ${cabinetId} introuvable.`));
  }
  return clientDossiers.load(access).pipe(
    map((dossier) => {
      dossierService.changeDossier(dossier);
      dossierService.storeCabinetId(access.cabinetId);
      return dossier;
    }),
  );
};

/**
 * Dossier du collaborateur : dossier, travail en cours et situations chargés ensemble ; il devient le seul dossier
 * connu et le dossier courant. Doit être appelé dans un contexte d'injection.
 */
function resoudreDossierCollab(id: number): Observable<DossierContext> {
  const dossierService = inject(DossierService);
  const collaborateurs = inject(CollaborateurService);
  const user = collaborateurs.user;
  const codeCollab = collaborateurs.collab?.codeCollab;
  return forkJoin([dossierService.get(id), dossierService.getTravail(id), dossierService.getSituations(id)]).pipe(
    map(([dossier, travail, situations]) => {
      const dossierContext = {
        ...dossier,
        travail,
        situations,
        isSaisieTravailAutorise: saisieTravailAutorisee(travail, dossier, user, codeCollab),
      } as DossierContext;
      dossierService.dossiers = [dossierContext];
      dossierService.changeDossier(dossierContext);
      return dossierContext;
    }),
  );
}
